import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { PrinterIcon, FilmIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ccApi, analyticsApi } from '../../services/api';
import { PHASE_META, type Operation, type OperationEvent, type ExerciseEvent, type PlatoonPhase } from '../../types/domain';

function safeTime(iso?: string | null, fmt = 'dd MMM yyyy HH:mm'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : format(d, fmt);
}

function fmtDuration(ms: number): string {
  if (ms <= 0) return '0m';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Phase durations from the operation's phase timeline: each event holds until the
// next phase change (last holds until completion / now).
function phaseDurations(events: OperationEvent[], endIso: string | null): { phase: PlatoonPhase; ms: number }[] {
  const ordered = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const out: { phase: PlatoonPhase; ms: number }[] = [];
  ordered.forEach((ev, i) => {
    const start = new Date(ev.timestamp).getTime();
    const next = i < ordered.length - 1 ? new Date(ordered[i + 1].timestamp).getTime() : end;
    out.push({ phase: ev.phase, ms: Math.max(0, next - start) });
  });
  return out;
}

interface Props {
  operation: Operation;
  unitLabel: string;
  onClose: () => void;
}

export default function AarReport({ operation: op, unitLabel, onClose }: Props) {
  const navigate = useNavigate();
  const fromIso = op.startedAt;
  const toIso = op.completedAt ?? new Date().toISOString();

  // War-diary events for this platoon within the operation window.
  const feedQ = useQuery({
    queryKey: ['aar-feed', op.id, op.platoonId, fromIso, toIso],
    queryFn: async () =>
      (await ccApi.getFeed({
        platoonId: op.platoonId,
        since: fromIso ?? undefined,
        until: toIso,
        limit: 300,
      })).data.events as ExerciseEvent[],
    retry: false,
  });

  // Distance summary (cheap movement rollup for the window). Tolerate absence.
  const moveQ = useQuery({
    queryKey: ['aar-move', op.platoonId, fromIso, toIso],
    queryFn: async () =>
      (await analyticsApi.getMovementSummary({
        startDate: fromIso ?? toIso,
        endDate: toIso,
        platoonId: op.platoonId,
      })).data,
    retry: false,
    enabled: !!fromIso,
  });

  const durations = useMemo(() => phaseDurations(op.events ?? [], op.completedAt), [op.events, op.completedAt]);
  const events = useMemo(
    () => [...(feedQ.data ?? [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
    [feedQ.data]
  );
  const distanceKm = moveQ.data?.summary?.totalDistanceKm;

  const replay = () => {
    const params = new URLSearchParams();
    if (op.platoonId) params.set('platoonId', op.platoonId);
    if (fromIso) params.set('from', fromIso);
    params.set('to', toIso);
    navigate(`/simulation?${params.toString()}`);
  };

  return (
    <div className="fixed inset-0 z-[900] bg-black/70 flex items-start justify-center p-4 overflow-y-auto no-print-backdrop">
      <div className="aar-printable w-full max-w-3xl my-8 bg-command-panel border border-command-border rounded-xl">
        {/* Toolbar (never printed) */}
        <div className="no-print flex items-center justify-between p-4 border-b border-command-border">
          <h2 className="text-base font-semibold text-gray-100">After-Action Review</h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={replay}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-info/20 text-command-info border border-command-info/40 hover:bg-command-info/30">
              <FilmIcon className="w-4 h-4" /> Replay in Simulation
            </button>
            <button type="button" onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90">
              <PrinterIcon className="w-4 h-4" /> Print AAR
            </button>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-command-border/60" aria-label="Close">
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Printable body */}
        <div className="p-6 space-y-5">
          <header className="border-b border-command-border pb-3">
            <h1 className="text-lg font-display font-bold text-gray-100">
              AFTER-ACTION REVIEW — {op.type}
            </h1>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div><span className="text-gray-500">Platoon: </span><span className="text-gray-200">{op.platoon?.callsign ?? '—'}</span></div>
              <div><span className="text-gray-500">Unit: </span><span className="text-gray-200">{unitLabel}</span></div>
              <div><span className="text-gray-500">Night: </span><span className="text-gray-200">{op.night ?? '—'}</span></div>
              <div><span className="text-gray-500">Status: </span><span className="text-gray-200">{op.status}</span></div>
              <div><span className="text-gray-500">Started: </span><span className="text-gray-200">{safeTime(op.startedAt)}</span></div>
              <div><span className="text-gray-500">Completed: </span><span className="text-gray-200">{safeTime(op.completedAt)}</span></div>
              {typeof distanceKm === 'number' && (
                <div><span className="text-gray-500">Distance: </span><span className="text-gray-200">{distanceKm.toFixed(1)} km</span></div>
              )}
            </div>
          </header>

          {/* Phase durations */}
          <section>
            <h3 className="text-sm font-semibold text-gray-100 mb-2">Phase Durations</h3>
            {durations.length === 0 ? (
              <p className="text-xs text-gray-500">No phase events logged.</p>
            ) : (
              <ul className="space-y-1">
                {durations.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-24 font-tactical" style={{ color: PHASE_META[d.phase]?.color }}>
                      {PHASE_META[d.phase]?.label ?? d.phase}
                    </span>
                    <span className="text-gray-300">{fmtDuration(d.ms)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Event timeline */}
          <section>
            <h3 className="text-sm font-semibold text-gray-100 mb-2">Event Timeline</h3>
            {feedQ.isError ? (
              <p className="text-xs text-gray-500">War-diary feed unavailable for this window.</p>
            ) : events.length === 0 ? (
              <p className="text-xs text-gray-500">No war-diary events in this window.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 text-left border-b border-command-border">
                    <th className="py-1 pr-2 font-semibold">Time</th>
                    <th className="py-1 pr-2 font-semibold">Type</th>
                    <th className="py-1 font-semibold">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-command-border/40">
                      <td className="py-1 pr-2 text-gray-400 font-tactical whitespace-nowrap">{safeTime(e.timestamp, 'HH:mm:ss')}</td>
                      <td className="py-1 pr-2 text-gray-300 whitespace-nowrap">{e.type}</td>
                      <td className="py-1 text-gray-200">{e.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <footer className="text-[10px] text-gray-500 border-t border-command-border pt-2">
            Generated {safeTime(new Date().toISOString())} · OpMonitor ST-2026
          </footer>
        </div>
      </div>
    </div>
  );
}
